import {html, LitElement, type PropertyValues} from "lit"
import {customElement, property} from "lit/decorators.js"
import {defaultSettings, type Settings} from "./models/fishman-verify-email.model"
import {a11Fetch, type A11SiteDataModel, type FetchProps} from "./utilities"

@customElement("fishman-verify-email")
export class FishmanVerifyEmail extends LitElement {
    @property({type: String})
    encodedSettings = ""

    @property({type: Object})
    settings: Settings = {...defaultSettings}

    @property({type: String})
    default_email_value = ""

    @property({type: Boolean})
    is_form_submitting = false

    @property({type: Boolean})
    is_successful = false

    @property({type: Boolean})
    has_error = false

    @property({type: String})
    error_message = ""

    override updated(changedProperties: PropertyValues) {
        if (!changedProperties.has("encodedSettings")) {
            return
        }

        try {
            const rawSettings = atob(this.encodedSettings) // decode base64 string
            this.settings = JSON.parse(rawSettings) // parse the JSON string into an object
        } catch (e) {
            console.error("Cannot parse settings:", e)
        }
    }

    override render() {
        // Destructure the settings object for easy access to the properties

        if (this.is_successful) {
            return html`
                <div class="alert alert-success my-5" role="alert" data-test="fishman-verify-email-success">
                    Your email has been verified successfully. Redirecting now...
                </div>
            `
        }

        return html`
            <form class="row py-3 g-3" @submit=${this.onSubmit}>
                <div class="col-12 d-flex flex-column gap-3">
                    <h1 class="fishman-verify-email__section-title text-center">
                        Verify Email Address
                    </h1>

                    <div class="alert alert-info mb-0" role="alert" data-test="fishman-verify-email-info">
                        A verification code has been sent to your email address. Please enter the code below to verify
                        your account.
                    </div>

                    <div>
                        <label for="email" class="form-label">Email address</label>
                        <input
                                type="email"
                                name="email_address"
                                id="email"
                                class="form-control"
                                .value=${this.default_email_value}
                                readonly
                                data-test="email"
                                required
                        >
                    </div>

                    <div>
                        <label for="verification_code" class="form-label">Verification code</label>
                        <input
                                type="text"
                                name="verification_code"
                                id="verification_code"
                                class="form-control"
                                data-test="verification-code"
                                .defaultValue=${this.settings.verificationCode || ""}
                                required
                        >
                    </div>

                    ${
                            this.is_form_submitting
                                    ? html`
                                        <div class="alert alert-info mb-0" role="alert"
                                             data-test="fishman-verify-email-verifying">
                                            Verifying your email...
                                        </div>
                                    `
                                    : ""
                    }

                    ${
                            this.has_error
                                    ? html`
                                        <div class="alert alert-danger mb-0" role="alert"
                                             data-test="fishman-verify-email-error">
                                            ${this.error_message}
                                        </div>
                                    `
                                    : ""
                    }

                    <p>
                        <button
                                type="submit"
                                class="btn btn-primary align-self-center"
                                ?disabled=${this.is_form_submitting}
                                data-test="submit"
                        >
                            Verify email
                        </button>
                    </p>

                    ${!this.settings.hideBackButton
                            ? html`
                                <p>
                                    <button
                                            class="btn btn-primary-outline"
                                            type="button"
                                            @click=${this._handleReturnToLoginClick}
                                            data-test="fishman-login-return-to-login"
                                    >
                                        Cancel
                                    </button>
                                </p>
                            `
                            : ""}
                </div>
            </form>
        `
    }

    override createRenderRoot() {
        return this
    }

    private _handleReturnToLoginClick = () => {
        const event = new CustomEvent("fishman-login-return-to-login", {
            detail: {current_user_name: this.default_email_value},
        })
        this.dispatchEvent(event)
    }

    private async onSubmit(e: Event) {
        e.preventDefault()
        this.is_form_submitting = true
        this.has_error = false
        this.error_message = ""

        const {block_id, redirect_url} = this.settings

        const form = e.target as HTMLFormElement
        const formData = new FormData(form)

        const fetchProperties: FetchProps = {
            action: "fishman_verify_email",
            block_id,
            method: "POST",
            body: formData,
        }

        const {errorMessage, statusCode} = await a11Fetch(fetchProperties)

        if (statusCode === 200) {
            this.is_successful = true

            // Redirect after a short delay to show success message
            setTimeout(() => {
                window.location.href = redirect_url || "/my-account/?verified=1"
            }, 1500)

            return
        }

        this.error_message = errorMessage || "Verification failed. Please check your code and try again."
        this.has_error = true
        this.is_form_submitting = false
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "fishman-verify-email": FishmanVerifyEmail
    }

    interface Window {
        ai_site_data: A11SiteDataModel
    }
}
